import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { CancelDialog } from "@/components/common/Modals"
import { FULL_LIST_LIMIT } from "@/constants/common"
import { SendDialogActions } from "@/screens/Pricing/components/dialogs/SendDialog/SendDialogActions"
import SendDialogTitle from "@/screens/Pricing/components/dialogs/SendDialog/SendDialogTitle"
import ViewRecipientAccordion from "@/screens/Pricing/components/dialogs/SendDialog/ViewRecipientAccordion"
import EmailTemplateForm from "@/screens/Pricing/components/forms/EmailTemplateForm"
import { DEFAULT_EMAIL_NOTE } from "@/screens/Pricing/constants/submitPriceChange"
import { useGetPriceChangePreview } from "@/screens/Pricing/hooks/useGetPriceChangePreview"
import { useGetPriceChangeRecipients } from "@/screens/Pricing/hooks/useGetPriceChangeRecepients"
import { useGetPriceChangeSubmission } from "@/screens/Pricing/hooks/useGetPriceChangeSubmission"
import type { SendPriceChangeSchemaFormValues } from "@/screens/Pricing/schema/SendPriceChangeSchema"
import { SendPriceChangeSchema } from "@/screens/Pricing/schema/SendPriceChangeSchema"
import { getPriceChangeGroupsCustomerIds } from "@/screens/Pricing/utils/priceChangeGroups"

interface ReSendPricingProps {
  onClose: () => void
  onSuccess: (submissionId: string, data: SendPriceChangeSchemaFormValues) => void
  submissionId: string
}

const ReSendAllCustomers: FC<ReSendPricingProps> = ({ onClose, onSuccess, submissionId }) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  // The recipient list is built from every customer in the submission, so this must
  // opt out of the review page's per-page limit.
  // TODO: AK-6472 Check if this is a performance problem for submissions with thousands of customers. If so, we may need to add a new query that returns only the customer IDs.
  const { priceChangeSubmission } = useGetPriceChangeSubmission({
    priceChangeId: submissionId,
    limit: FULL_LIST_LIMIT,
  })

  const customerIds = useMemo(
    () => getPriceChangeGroupsCustomerIds(priceChangeSubmission?.priceChangeItems),
    [priceChangeSubmission?.priceChangeItems]
  )

  const { recipients, isFetching, totalAccounts, totalContacts } = useGetPriceChangeRecipients({
    customerIds,
  })

  const methods = useForm<SendPriceChangeSchemaFormValues>({
    defaultValues: {
      emailNote: DEFAULT_EMAIL_NOTE,
    },
    mode: "onChange",
    resolver: zodResolver(SendPriceChangeSchema),
  })

  const {
    formState: { isValid, isSubmitted },
  } = methods

  const { handlePreview, isPreviewFetching } = useGetPriceChangePreview(submissionId)

  const confirmCancel = async () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSend = (data: SendPriceChangeSchemaFormValues) => {
    onSuccess(submissionId, data)
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth maxWidth='lg'>
        <>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent>
            <SendDialogTitle
              totalContacts={totalContacts}
              totalAccounts={totalAccounts}
              isFetching={isFetching}
              title='Resend price change notification'
            />

            <ViewRecipientAccordion recipients={recipients} isFetching={isFetching} />

            <FormProvider {...methods}>
              <EmailTemplateForm />
            </FormProvider>
          </DialogContent>
          <SendDialogActions
            submitLabel='Resend'
            isValid={isValid}
            isSubmitted={isSubmitted}
            isPreviewFetching={isPreviewFetching}
            onCancel={handleCancel}
            onPreview={() => handlePreview(methods.getValues().emailNote)}
            onSubmit={methods.handleSubmit(handleSend)}
          />
        </>
      </Dialog>
    </>
  )
}

export default ReSendAllCustomers
